/**
 * Registration Tokens Management Component
 *
 * Provides an interface for managing node registration tokens:
 * - List all tokens with status (active/expired/exhausted)
 * - Create new tokens with options (expiration, max uses, pre-assign tenant)
 * - Copy token (shown once on creation)
 * - Revoke tokens
 *
 * Access: GLOBAL_ADMIN only
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getApiClient, isSuccess } from '../../api/client';
import { Layout } from '../layout/Layout';

// =============================================================================
// Types
// =============================================================================

type TokenStatus = 'active' | 'expired' | 'exhausted';

interface RegistrationToken {
  id: string;
  name: string;
  expiresAt: string;
  usedAt: string | null;
  usedByNodeId: string | null;
  isGlobal: boolean;
  preAssignTenant: string | null;
  maxUses: number;
  useCount: number;
  createdAt: string;
  status: TokenStatus;
}

interface TokensResponse {
  tokens: RegistrationToken[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

interface TokenStatsResponse {
  totalTokens: number;
  activeTokens: number;
  expiredTokens: number;
  fullyUsedTokens: number;
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
}

interface CreateTokenFormData {
  name: string;
  expiresInHours: number;
  preAssignTenant: string | null;
  isGlobal: boolean;
  maxUses: number;
}

type ModalState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'created'; token: string; name: string }
  | { type: 'revoke'; token: RegistrationToken };

// =============================================================================
// Constants
// =============================================================================

const STATUS_COLORS = {
  active: { bg: 'bg-green-900/20', text: 'text-green-400', dot: 'bg-green-400' },
  expired: { bg: 'bg-red-900/20', text: 'text-red-400', dot: 'bg-red-400' },
  exhausted: { bg: 'bg-yellow-900/20', text: 'text-yellow-400', dot: 'bg-yellow-400' },
} as const;

const EXPIRATION_OPTIONS = [
  { value: 1, label: '1 hour' },
  { value: 4, label: '4 hours' },
  { value: 24, label: '24 hours' },
  { value: 72, label: '3 days' },
  { value: 168, label: '1 week' },
  { value: 720, label: '30 days' },
];

// =============================================================================
// Sub-components
// =============================================================================

function StatusBadge({ status }: { status: TokenStatus }) {
  const colors = STATUS_COLORS[status];
  const labels: Record<TokenStatus, string> = {
    active: 'Active',
    expired: 'Expired',
    exhausted: 'Exhausted',
  };

  return (
    <div className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}>
      <div className={`h-1.5 w-1.5 rounded-full ${colors.dot}`} />
      {labels[status]}
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-4">
      <p className="text-sm text-gray-400">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${color}`}>{value}</p>
    </div>
  );
}

function CopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className={`flex items-center gap-2 rounded px-3 py-1.5 text-sm transition-colors ${
        copied
          ? 'bg-green-600 text-white'
          : 'bg-white/10 text-gray-300 hover:bg-white/20'
      }`}
    >
      {copied ? (
        <>
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Copied!
        </>
      ) : (
        <>
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          {label || 'Copy'}
        </>
      )}
    </button>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function RegistrationTokens() {
  const { user } = useAuth();
  // Use useMemo to ensure stable api reference
  const api = useMemo(() => getApiClient(), []);

  // State
  const [tokens, setTokens] = useState<RegistrationToken[]>([]);
  const [stats, setStats] = useState<TokenStatsResponse | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>({ type: 'none' });
  const [includeExpired, setIncludeExpired] = useState(false);

  // Form state for create modal
  const [formData, setFormData] = useState<CreateTokenFormData>({
    name: '',
    expiresInHours: 24,
    preAssignTenant: null,
    isGlobal: true,
    maxUses: 1,
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Check permissions
  const isGlobalAdmin = user?.role === 'GLOBAL_ADMIN';

  // Fetch tokens
  const fetchTokens = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get<TokensResponse>(
        `/nodes/registration-tokens?includeExpired=${includeExpired}`
      );

      if (isSuccess(response)) {
        setTokens(response.data.tokens);
      } else {
        setError('Failed to load registration tokens');
      }
    } catch (err) {
      setError('Failed to load registration tokens');
    } finally {
      setLoading(false);
    }
  }, [api, includeExpired]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get<TokenStatsResponse>(
        '/nodes/registration-tokens/stats'
      );

      if (isSuccess(response)) {
        setStats(response.data);
      }
    } catch {
      // Stats are non-critical, don't show error
    }
  }, [api]);

  // Fetch tenants for dropdown
  const fetchTenants = useCallback(async () => {
    try {
      const response = await api.get<{ tenants: Tenant[] }>('/tenants');

      if (isSuccess(response)) {
        setTenants(response.data.tenants);
      }
    } catch {
      // Non-critical
    }
  }, [api]);

  // Initial load - component is inside ProtectedRoute so auth is guaranteed
  useEffect(() => {
    fetchStats();
    fetchTenants();
    fetchTokens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch tokens when includeExpired changes
  useEffect(() => {
    // Skip initial render (handled above)
    if (loading) return;
    fetchTokens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeExpired]);

  // Handle create token
  const handleCreate = async () => {
    setFormLoading(true);
    setFormError(null);

    try {
      const response = await api.post<{
        success: boolean;
        data: {
          id: string;
          name: string;
          token: string;
          expiresAt: string;
        };
      }>('/nodes/registration-tokens', formData);

      if (isSuccess(response) && response.data.success) {
        const { token, name } = response.data.data;
        setModal({ type: 'created', token, name });
        setFormData({
          name: '',
          expiresInHours: 24,
          preAssignTenant: null,
          isGlobal: true,
          maxUses: 1,
        });
        fetchTokens();
        fetchStats();
      } else {
        setFormError('Failed to create token');
      }
    } catch (err) {
      setFormError('Failed to create token');
    } finally {
      setFormLoading(false);
    }
  };

  // Handle revoke token
  const handleRevoke = async (tokenId: string) => {
    try {
      const response = await api.delete<{ success: boolean }>(
        `/nodes/registration-tokens/${tokenId}`
      );

      if (isSuccess(response) && response.data.success) {
        setModal({ type: 'none' });
        fetchTokens();
        fetchStats();
      } else {
        setError('Failed to revoke token');
      }
    } catch {
      setError('Failed to revoke token');
    }
  };

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  // Format relative time
  const formatRelative = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffHours = Math.round(diffMs / (1000 * 60 * 60));

    if (diffHours < 0) {
      return `${Math.abs(diffHours)}h ago`;
    } else if (diffHours < 24) {
      return `in ${diffHours}h`;
    } else {
      const diffDays = Math.round(diffHours / 24);
      return `in ${diffDays}d`;
    }
  };

  if (!isGlobalAdmin) {
    return (
      <Layout>
        <div className="flex h-full items-center justify-center">
          <p className="text-gray-400">Access denied. Global admin privileges required.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-white">Registration Tokens</h1>
            <p className="mt-1 text-sm text-gray-400">
              Manage tokens for remote node self-registration
            </p>
          </div>
          <button
            onClick={() => setModal({ type: 'create' })}
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-500"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Token
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-4 gap-4">
            <StatCard label="Total Tokens" value={stats.totalTokens} color="text-white" />
            <StatCard label="Active" value={stats.activeTokens} color="text-green-400" />
            <StatCard label="Expired" value={stats.expiredTokens} color="text-red-400" />
            <StatCard label="Fully Used" value={stats.fullyUsedTokens} color="text-yellow-400" />
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={includeExpired}
              onChange={(e) => setIncludeExpired(e.target.checked)}
              className="rounded border-gray-600 bg-gray-800 text-indigo-600 focus:ring-indigo-500"
            />
            Show expired tokens
          </label>
        </div>

        {/* Error */}
        {error && (
          <div className="rounded-lg border border-red-500/50 bg-red-500/10 p-4 text-red-400">
            {error}
          </div>
        )}

        {/* Token List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
          </div>
        ) : tokens.length === 0 ? (
          <div className="rounded-lg border border-white/10 bg-white/5 p-8 text-center">
            <p className="text-gray-400">No registration tokens found</p>
            <p className="mt-2 text-sm text-gray-500">
              Create a token to allow remote nodes to self-register
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-white/10">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-400">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-400">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-400">Uses</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-400">Scope</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-400">Expires</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-400">Created</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {tokens.map((token) => (
                  <tr key={token.id} className="hover:bg-white/5">
                    <td className="px-4 py-3">
                      <span className="font-medium text-white">{token.name}</span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={token.status} />
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      {token.useCount} / {token.maxUses}
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      {token.isGlobal ? (
                        <span className="text-blue-400">Global</span>
                      ) : (
                        <span className="text-purple-400">Tenant</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      <span title={formatDate(token.expiresAt)}>
                        {formatRelative(token.expiresAt)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-sm">
                      {formatDate(token.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {token.status === 'active' && (
                        <button
                          onClick={() => setModal({ type: 'revoke', token })}
                          className="text-red-400 hover:text-red-300 text-sm"
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create Modal */}
        {modal.type === 'create' && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-lg border border-white/10 bg-gray-900 p-6">
              <h2 className="text-lg font-semibold text-white">Create Registration Token</h2>
              <p className="mt-1 text-sm text-gray-400">
                Generate a token for remote node self-registration
              </p>

              <div className="mt-6 space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-300">Token Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., GPU Server Setup"
                    className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white placeholder-gray-500 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>

                {/* Expiration */}
                <div>
                  <label className="block text-sm font-medium text-gray-300">Expires In</label>
                  <select
                    value={formData.expiresInHours}
                    onChange={(e) => setFormData({ ...formData, expiresInHours: Number(e.target.value) })}
                    className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    {EXPIRATION_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Max Uses */}
                <div>
                  <label className="block text-sm font-medium text-gray-300">Maximum Uses</label>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={formData.maxUses}
                    onChange={(e) => setFormData({ ...formData, maxUses: Number(e.target.value) })}
                    className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>

                {/* Global checkbox */}
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.isGlobal}
                      onChange={(e) => setFormData({ ...formData, isGlobal: e.target.checked, preAssignTenant: null })}
                      className="rounded border-gray-600 bg-gray-800 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-300">Register as global node</span>
                  </label>
                </div>

                {/* Pre-assign tenant */}
                {!formData.isGlobal && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Pre-assign to Tenant</label>
                    <select
                      value={formData.preAssignTenant || ''}
                      onChange={(e) => setFormData({ ...formData, preAssignTenant: e.target.value || null })}
                      className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-white focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                      <option value="">Select a tenant...</option>
                      {tenants.map((tenant) => (
                        <option key={tenant.id} value={tenant.id}>
                          {tenant.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {formError && (
                  <div className="text-sm text-red-400">{formError}</div>
                )}
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setModal({ type: 'none' })}
                  className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={formLoading || !formData.name}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
                >
                  {formLoading ? 'Creating...' : 'Create Token'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Token Created Modal */}
        {modal.type === 'created' && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-lg rounded-lg border border-white/10 bg-gray-900 p-6">
              <div className="flex items-center gap-3 text-green-400">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h2 className="text-lg font-semibold">Token Created</h2>
              </div>

              <div className="mt-4">
                <p className="text-sm text-gray-400">
                  Token "{modal.name}" has been created. Copy the token below - it will only be shown once.
                </p>
              </div>

              <div className="mt-4 rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-4">
                <div className="flex items-center justify-between gap-4">
                  <code className="break-all text-sm text-yellow-300">{modal.token}</code>
                  <CopyButton text={modal.token} label="Copy Token" />
                </div>
              </div>

              <div className="mt-4 rounded-lg bg-white/5 p-4">
                <h3 className="text-sm font-medium text-white">Remote Node Setup</h3>
                <p className="mt-2 text-sm text-gray-400">
                  On the remote node, set these environment variables:
                </p>
                <div className="mt-2 rounded bg-black/30 p-3">
                  <code className="text-xs text-gray-300">
                    REGISTRATION_TOKEN={modal.token}<br />
                    MAIN_SYSTEM_URL=https://your-system-url<br />
                    NODE_NAME=your-node-name
                  </code>
                </div>
                <p className="mt-2 text-sm text-gray-400">
                  Then run: <code className="text-indigo-400">python -m registration</code>
                </p>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setModal({ type: 'none' })}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Revoke Confirmation Modal */}
        {modal.type === 'revoke' && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-lg border border-white/10 bg-gray-900 p-6">
              <div className="flex items-center gap-3 text-red-400">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <h2 className="text-lg font-semibold">Revoke Token</h2>
              </div>

              <p className="mt-4 text-gray-300">
                Are you sure you want to revoke the token "{modal.token.name}"?
              </p>
              <p className="mt-2 text-sm text-gray-400">
                Any nodes that haven't registered yet will no longer be able to use this token.
              </p>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setModal({ type: 'none' })}
                  className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleRevoke(modal.token.id)}
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-500"
                >
                  Revoke Token
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default RegistrationTokens;
